import type { EventBus } from '@/core/EventBus';

/**
 * Game phases
 */
export enum GamePhase {
  MainMenu = 'mainMenu',
  RulerSelect = 'rulerSelect',
  Preparation = 'preparation',
  Battle = 'battle',
  RoundEnd = 'roundEnd',
  MatchEnd = 'matchEnd',
}

/**
 * Game state events
 */
export const GameStateEvents = {
  PHASE_CHANGED: 'gameState:phaseChanged',
  ROUND_STARTED: 'gameState:roundStarted',
  ROUND_ENDED: 'gameState:roundEnded',
  MATCH_ENDED: 'gameState:matchEnded',
  TIMER_TICK: 'gameState:timerTick',
} as const;

export interface PhaseChangedEvent {
  previousPhase: GamePhase;
  newPhase: GamePhase;
}

export interface RoundEndedEvent {
  roundNumber: number;
  winnerId: number; // 0 = draw
  player1Score: number;
  player2Score: number;
}

export interface MatchEndedEvent {
  winnerId: number;
  player1Score: number;
  player2Score: number;
  totalRounds: number;
}

/**
 * Game configuration
 */
export interface GameConfig {
  roundsToWin: number;
  prepTime: number; // seconds
  maxRoundTime: number; // seconds (0 = unlimited)
  roundEndDelay: number; // seconds before auto-proceeding after round end
}

export const DEFAULT_GAME_CONFIG: GameConfig = {
  roundsToWin: 3, // Best of 5
  prepTime: 12,
  maxRoundTime: 120,
  roundEndDelay: 3,
};

/**
 * Current game state
 */
export interface GameState {
  phase: GamePhase;
  currentRound: number;
  player1Score: number;
  player2Score: number;
  phaseTimer: number;
  phaseTimeLimit: number;
}

/**
 * Manages overall game state and phase transitions
 */
export class GameStateManager {
  private eventBus: EventBus;
  private config: GameConfig;
  private state: GameState;

  constructor(eventBus: EventBus, config: GameConfig = DEFAULT_GAME_CONFIG) {
    this.eventBus = eventBus;
    this.config = config;

    this.state = {
      phase: GamePhase.MainMenu,
      currentRound: 0,
      player1Score: 0,
      player2Score: 0,
      phaseTimer: 0,
      phaseTimeLimit: 0,
    };
  }

  /**
   * Get current game state
   */
  getState(): Readonly<GameState> {
    return this.state;
  }

  /**
   * Get current phase
   */
  get phase(): GamePhase {
    return this.state.phase;
  }

  /**
   * Get current round number
   */
  get currentRound(): number {
    return this.state.currentRound;
  }

  /**
   * Set current round number (for multiplayer sync)
   */
  set currentRound(value: number) {
    this.state.currentRound = value;
  }

  /**
   * Set player 1 score (for multiplayer sync)
   */
  set player1Score(value: number) {
    this.state.player1Score = value;
  }

  /**
   * Set player 2 score (for multiplayer sync)
   */
  set player2Score(value: number) {
    this.state.player2Score = value;
  }

  /**
   * Set phase timer (for multiplayer sync)
   */
  setPhaseTimer(current: number, limit: number): void {
    this.state.phaseTimer = current;
    this.state.phaseTimeLimit = limit;
  }

  /**
   * Get scores
   */
  getScores(): { player1: number; player2: number } {
    return {
      player1: this.state.player1Score,
      player2: this.state.player2Score,
    };
  }

  /**
   * Get remaining phase time
   */
  getRemainingTime(): number {
    return Math.max(0, this.state.phaseTimeLimit - this.state.phaseTimer);
  }

  /**
   * Get game configuration
   */
  getConfig(): GameConfig {
    return this.config;
  }

  /**
   * Start a new match
   */
  startMatch(): void {
    this.state.currentRound = 0;
    this.state.player1Score = 0;
    this.state.player2Score = 0;

    this.setPhase(GamePhase.RulerSelect);
  }

  /**
   * Skip ruler selection (for quick play)
   */
  skipRulerSelect(): void {
    if (this.state.phase === GamePhase.RulerSelect) {
      this.startRound();
    }
  }

  /**
   * Start a new round
   */
  startRound(): void {
    this.state.currentRound++;
    this.state.phaseTimer = 0;
    this.state.phaseTimeLimit = this.config.prepTime;

    this.setPhase(GamePhase.Preparation);

    this.eventBus.emit(GameStateEvents.ROUND_STARTED, {
      roundNumber: this.state.currentRound,
    });

    console.log(`Round ${this.state.currentRound} started`);
  }

  /**
   * Start battle phase
   */
  startBattle(): void {
    if (this.state.phase !== GamePhase.Preparation) {
      console.warn('Cannot start battle from phase:', this.state.phase);
      return;
    }

    this.state.phaseTimer = 0;
    this.state.phaseTimeLimit = this.config.maxRoundTime;

    this.setPhase(GamePhase.Battle);
  }

  /**
   * End current round with a winner
   */
  endRound(winnerId: number, skipScoreUpdate: boolean = false): void {
    if (this.state.phase !== GamePhase.Battle) {
      console.warn('Cannot end round from phase:', this.state.phase);
      return;
    }

    // Update scores (skip in multiplayer - server is authoritative)
    if (!skipScoreUpdate) {
      if (winnerId === 1) {
        this.state.player1Score++;
      } else if (winnerId === 2) {
        this.state.player2Score++;
      }
      // Draw (0) doesn't change scores
    }

    this.setPhase(GamePhase.RoundEnd);

    this.eventBus.emit(GameStateEvents.ROUND_ENDED, {
      roundNumber: this.state.currentRound,
      winnerId,
      player1Score: this.state.player1Score,
      player2Score: this.state.player2Score,
    } as RoundEndedEvent);

    console.log(`Round ${this.state.currentRound} ended. Winner: Player ${winnerId || 'Draw'}`);
    console.log(`Score: P1 ${this.state.player1Score} - P2 ${this.state.player2Score}`);
  }

  /**
   * Check if match is over
   */
  isMatchOver(): boolean {
    return (
      this.state.player1Score >= this.config.roundsToWin ||
      this.state.player2Score >= this.config.roundsToWin
    );
  }

  /**
   * Get match winner (or 0 if not over)
   */
  getMatchWinner(): number {
    if (this.state.player1Score >= this.config.roundsToWin) return 1;
    if (this.state.player2Score >= this.config.roundsToWin) return 2;
    return 0;
  }

  /**
   * Proceed after round end
   */
  proceedAfterRound(): void {
    if (this.state.phase !== GamePhase.RoundEnd) return;

    if (this.isMatchOver()) {
      this.endMatch();
    } else {
      this.startRound();
    }
  }

  /**
   * End the match
   */
  endMatch(): void {
    const winner = this.getMatchWinner();

    this.setPhase(GamePhase.MatchEnd);

    this.eventBus.emit(GameStateEvents.MATCH_ENDED, {
      winnerId: winner,
      player1Score: this.state.player1Score,
      player2Score: this.state.player2Score,
      totalRounds: this.state.currentRound,
    } as MatchEndedEvent);

    console.log(`Match ended! Winner: Player ${winner}`);
  }

  /**
   * Return to main menu
   */
  returnToMainMenu(): void {
    this.setPhase(GamePhase.MainMenu);
  }

  /**
   * Update timer (call each frame during timed phases)
   */
  updateTimer(deltaTime: number): boolean {
    if (
      this.state.phase !== GamePhase.Preparation &&
      this.state.phase !== GamePhase.Battle
    ) {
      return false;
    }

    const previousTime = this.state.phaseTimer;
    // deltaTime is already in seconds from the game loop
    this.state.phaseTimer += deltaTime;

    // Emit timer tick every second
    if (Math.floor(previousTime) !== Math.floor(this.state.phaseTimer)) {
      this.eventBus.emit(GameStateEvents.TIMER_TICK, {
        remaining: this.getRemainingTime(),
        phase: this.state.phase,
      });
    }

    // Check for time expiration
    if (
      this.state.phaseTimeLimit > 0 &&
      this.state.phaseTimer >= this.state.phaseTimeLimit
    ) {
      return true; // Time expired
    }

    return false;
  }

  /**
   * Set phase and emit event
   * Made public for multiplayer server-controlled phase transitions
   */
  setPhase(newPhase: GamePhase): void {
    const previousPhase = this.state.phase;
    this.state.phase = newPhase;

    this.eventBus.emit(GameStateEvents.PHASE_CHANGED, {
      previousPhase,
      newPhase,
    } as PhaseChangedEvent);
  }

  /**
   * Force set phase (for testing/debugging)
   */
  forcePhase(phase: GamePhase): void {
    this.setPhase(phase);
  }
}
